import {ChangeDriverOption, OtherDriver, RowData, SwapRouteOption} from "../types/types";
import {RoutePlanningDriver} from "../../../providers/RoutePlanningProvider";
import {toVehicleId} from "./RoutePlanning.utils";

const driverLabelOf = (driver: {name?: string | null; driverCode?: string | null; driverId?: number | null}) =>
  driver.name ?? driver.driverCode ?? `Driver ${driver.driverId}`;

// DP-3039: route labels follow the Move-Job dialog convention — "Route {n}" by the route's
// 1-based position in workingRowData (NOT a fixed A/B). Same numbering as MoveJobDialog's target
// dropdown, so a run with 3+ routes reads Route 1 / Route 2 / Route 3 instead of every other route
// collapsing to "Route B".
const routeLabelAt = (rows: RowData[], routeId: string): string | null => {
  const index = rows.findIndex((row) => row.routeId === routeId);
  return index >= 0 ? `Route ${index + 1}` : null;
};

export type SwapChangeOptions = {
  currentDriverName: string;
  currentRouteLabel: string;
  /** Swap targets — one option per OTHER route row, excluding this route's own row. */
  swapOptions: SwapRouteOption[];
  /** Change · "Currently selected" — in-run drivers minus this route's driver. */
  currentlySelectedOptions: ChangeDriverOption[];
  /** Change · "Other drivers" — active drivers outside the run and not owning a route. */
  otherDriverOptions: ChangeDriverOption[];
};

/**
 * DP-3039: compute the Swap-or-Change dialog options for the route being acted on. Lives in a
 * pure helper (used by DriverResultList, which owns the single lifted-out dialog) so it always
 * reads the FRESH working rows — route ownership/labels come from `rows`, not stale grid data.
 */
export const buildSwapChangeOptions = (
  routeId: string,
  rows: RowData[],
  selectedDrivers: RoutePlanningDriver[],
  otherDrivers: OtherDriver[]
): SwapChangeOptions => {
  const currentRow = rows.find((row) => row.routeId === routeId);
  const currentVehicleId = currentRow ? toVehicleId(currentRow) : undefined;
  const currentDriverName = currentRow ? driverLabelOf(currentRow) : "";
  const currentRouteLabel = routeLabelAt(rows, routeId) ?? "Route 1";

  // vehicleId -> the routeId they currently own, for the "currently on Route N" hint.
  // Uses vehicleId (unique per route) instead of driverId to handle same-driver multi-manifest.
  const routeByVehicleId = new Map<string, string>();
  rows.forEach((row) => {
    routeByVehicleId.set(toVehicleId(row), row.routeId);
  });

  // Set of driverIds that already own a route — for filtering "Other drivers"
  const driversInRun = new Set(rows.map((row) => row.driverId).filter(Boolean));

  // Exclude routes owned by the same physical driver — swapping driver identity
  // between two routes of the same driver is a no-op. For multi-manifest, both
  // manifest routes share the same driverId, so both are excluded.
  const currentDriverId = currentRow?.driverId;
  // DP-3175: Exclude existing manifest routes from swap options — manifest routes
  // cannot have their driver swapped.
  const swapOptions: SwapRouteOption[] = rows
    .filter((row) => row.routeId !== routeId && row.driverId !== currentDriverId && !row.manifestDriverId)
    .map((row) => ({
      routeId: row.routeId,
      driverLabel: driverLabelOf(row),
      routeLabel: `Route ${rows.indexOf(row) + 1}`
    }));

  const currentlySelectedOptions: ChangeDriverOption[] = selectedDrivers
    .filter((driver) => driver.driverId != null && toVehicleId(driver) !== currentVehicleId)
    .map((driver) => {
      const ownedRouteId = routeByVehicleId.get(toVehicleId(driver));
      return {
        driverId: driver.driverId!,
        manifestDriverId: driver.manifestDriverId ?? undefined,
        driverLabel: driverLabelOf(driver),
        routeLabel: ownedRouteId ? routeLabelAt(rows, ownedRouteId) : null
      };
    });

  const otherDriverOptions: ChangeDriverOption[] = otherDrivers
    .filter((driver) => driver.driverId !== currentRow?.driverId && !driversInRun.has(driver.driverId))
    .map((driver) => ({driverId: driver.driverId, driverLabel: driverLabelOf(driver), routeLabel: null}));

  return {currentDriverName, currentRouteLabel, swapOptions, currentlySelectedOptions, otherDriverOptions};
};
